import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/mutations"

import {
  ConfirmWorkOrderDocument,
  type ConfirmWorkOrderFailure,
  type ConfirmWorkOrderInput,
  type ConfirmWorkOrderMutation,
  type ConfirmWorkOrderMutationVariables,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseConfirmAdjustmentWorkOrderReturn {
  confirmWorkOrder: (inventoryAdjustmentId: string) => Promise<boolean>
  isConfirming: boolean
}

export const useConfirmAdjustmentWorkOrder = (): UseConfirmAdjustmentWorkOrderReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<ConfirmWorkOrderMutation, ConfirmWorkOrderMutationVariables>(
    ConfirmWorkOrderDocument
  )

  const confirmWorkOrder = useCallback(
    async (inventoryAdjustmentId: string): Promise<boolean> => {
      const { data, error } = await executeMutation({ input: { inventoryAdjustmentId } as ConfirmWorkOrderInput })
      const output = data?.inventoryAdjustment.confirmWorkOrder
      const hasError = error || output?.__typename === "ConfirmWorkOrderFailure"

      if (hasError) {
        const message = error?.message ?? (output as ConfirmWorkOrderFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }

      notify({ type: NotificationType.Success, message: "Work order confirmed" })
      return true
    },
    [executeMutation, notify]
  )

  return { confirmWorkOrder, isConfirming: fetching }
}
